import { type FC, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import UserAvatar from '@/components/base/UserAvatar';
import { useAuth } from '@/hooks/useAuth';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
  section?: string;
  roles?: Array<'admin' | 'lawyer' | 'secretary'>;
}

const sectionForPath: Record<string, string> = {
  '/today': 'INÍCIO', '/dashboard': 'INÍCIO', '/attention': 'INÍCIO',
  '/leads': 'RELACIONAMENTO', '/clients': 'RELACIONAMENTO', '/messages': 'RELACIONAMENTO',
  '/cases': 'JURÍDICO', '/publications': 'JURÍDICO', '/deadlines': 'JURÍDICO', '/tasks': 'JURÍDICO', '/agenda': 'JURÍDICO', '/checklists': 'JURÍDICO',
  '/documents': 'DOCUMENTOS', '/templates': 'DOCUMENTOS',
  '/products': 'COMERCIAL', '/fees': 'COMERCIAL', '/finance': 'FINANCEIRO',
  '/ai': 'CONHECIMENTO E FERRAMENTAS', '/library': 'CONHECIMENTO E FERRAMENTAS', '/calculators': 'CONHECIMENTO E FERRAMENTAS', '/sites': 'CONHECIMENTO E FERRAMENTAS', '/reports': 'CONHECIMENTO E FERRAMENTAS',
  '/integrations': 'ADMINISTRAÇÃO', '/settings': 'ADMINISTRAÇÃO', '/backup': 'ADMINISTRAÇÃO', '/users': 'ADMINISTRAÇÃO', '/import-export': 'ADMINISTRAÇÃO', '/portal': 'ADMINISTRAÇÃO',
};

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: 'ri-dashboard-line' },
  { label: 'Hoje', path: '/today', icon: 'ri-sun-line' },
  { label: 'Leads / CRM', path: '/leads', icon: 'ri-user-search-line' },
  { label: 'Clientes', path: '/clients', icon: 'ri-team-line' },
  { label: 'Casos', path: '/cases', icon: 'ri-scales-line' },
  { label: 'Produtos Jurídicos', path: '/products', icon: 'ri-stack-line' },
  { label: 'Honorários', path: '/fees', icon: 'ri-money-dollar-circle-line' },
  { label: 'Documentos', path: '/documents', icon: 'ri-file-text-line' },
  { label: 'Modelos', path: '/templates', icon: 'ri-file-copy-line' },
  { label: 'Tarefas', path: '/tasks', icon: 'ri-todo-line' },
  { label: 'Financeiro', path: '/finance', icon: 'ri-bank-card-line' },
  { label: 'Calculadoras', path: '/calculators', icon: 'ri-calculator-line' },
  { label: 'Agenda', path: '/agenda', icon: 'ri-calendar-line' },
  { label: 'Publicações', path: '/publications', icon: 'ri-newspaper-line' },
  { label: 'Mensagens', path: '/messages', icon: 'ri-chat-3-line' },
  { label: 'IA e Prompts', path: '/ai', icon: 'ri-brain-line' },
  { label: 'Biblioteca', path: '/library', icon: 'ri-book-open-line' },
  { label: 'Relatórios', path: '/reports', icon: 'ri-bar-chart-line' },
  { label: 'Backup', path: '/backup', icon: 'ri-cloud-line', roles: ['admin'] },
  { label: 'Sites Jurídicos', path: '/sites', icon: 'ri-links-line' },
  { label: 'Prazos', path: '/deadlines', icon: 'ri-time-line' },
  { label: 'Checklists', path: '/checklists', icon: 'ri-checkbox-multiple-line' },
  { label: 'Configurações', path: '/settings', icon: 'ri-settings-3-line', roles: ['admin'] },
  { label: 'Usuários e Acessos', path: '/users', icon: 'ri-user-settings-line', roles: ['admin'] },
  { label: 'Integrações', path: '/integrations', icon: 'ri-link', roles: ['admin'] },
  { label: 'Portal do Cliente', path: '/portal', icon: 'ri-window-line' },
];

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'BM';

const Sidebar: FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const currentUser = useMemo(() => user ?? { name: 'Usuário autenticado' }, [user]);
  const currentInitials = useMemo(() => (user ? getInitials(user.name) : 'BM'), [user]);

  const visibleNavItems = useMemo(
    () =>
      navItems.filter(
        (item) =>
          !item.roles ||
          Boolean(user && item.roles.includes(user.role))
      ),
    [user]
  );

  const isActive = (path: string) => location.pathname === path;
  const visibleSections = useMemo(() => Array.from(new Set(visibleNavItems.map((item) => sectionForPath[item.path] ?? 'OUTROS'))), [visibleNavItems]);

  return (
    <>
      {/* Mobile backdrop */}
      {!collapsed && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onToggle}></div>
      )}

      <aside
        className={`fixed top-0 left-0 h-full z-50 bg-background-50 border-r border-background-200 flex flex-col transition-all duration-300 ${
          collapsed ? '-translate-x-full lg:translate-x-0 lg:w-[72px]' : 'translate-x-0 w-[260px]'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-background-200">
          {!collapsed ? (
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
                <span className="text-background-50 font-bold text-xs">BM</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-900 whitespace-nowrap">BM Legal OS</p>
                <p className="text-[10px] text-foreground-400 whitespace-nowrap">Borges Meschick</p>
              </div>
            </div>
          ) : (
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center mx-auto">
              <span className="text-background-50 font-bold text-xs">BM</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {visibleSections.map((section) => <div key={section} className="mb-2">
            {!collapsed && <button type="button" onClick={() => setCollapsedSections((current) => ({ ...current, [section]: !current[section] }))} className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold tracking-wider text-foreground-400 cursor-pointer"><span>{section}</span><i className={collapsedSections[section] ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'}></i></button>}
            {!collapsedSections[section] && visibleNavItems.filter((item) => (sectionForPath[item.path] ?? 'OUTROS') === section).map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                if (window.innerWidth < 1024) onToggle();
              }}
              title={collapsed ? item.label : undefined}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 mb-0.5 whitespace-nowrap cursor-pointer ${
                isActive(item.path)
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
              } ${collapsed ? 'justify-center px-0' : ''}`}
            >
              <i className={`${item.icon} text-lg flex-shrink-0`}></i>
              {!collapsed && <span className="truncate">{item.label}</span>}
              {item.label === 'Portal do Cliente' && !collapsed && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 whitespace-nowrap">Futuro</span>
              )}
            </button>
          ))}
          </div>)}
        </nav>

        {/* User */}
        <div className={`border-t border-background-200 ${collapsed ? 'p-2' : 'p-3'}`}>
          {collapsed ? (
            <div className="flex justify-center">
              <UserAvatar initials={currentInitials} name={currentUser.name} size="sm" onClick={() => setShowProfileMenu(!showProfileMenu)} />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <UserAvatar initials={currentInitials} name={currentUser.name} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground-800 truncate">{currentUser.name}</p>
                <p className="text-[10px] text-foreground-400 truncate">
                  {user?.role === 'admin'
                    ? 'Administrador'
                    : user?.role === 'lawyer'
                      ? 'Advogado'
                      : user?.role === 'secretary'
                        ? 'Secretaria'
                        : 'Usuário autenticado'}
                </p>
              </div>
              <button
                onClick={async () => { await logout(); navigate('/login'); }}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                title="Sair"
              >
                <i className="ri-logout-box-line text-sm"></i>
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
